/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  BulkUploadTask,
  BulkUploadTaskStatus,
  UploadType,
} from "types/delv-platform-api/bulkUploadTask"
import { useCallback, useEffect, useRef, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { actions, actionTypes } from "store/duck/bulkUpload/actions"
import {
  BULK_TASK_STOP_POLLING_IDLE_WAIT_TIME,
  BULK_UPLOAD_TABLE_RECORD_LIMIT,
} from "constants/menu_bulk_upload"
import _forIn from "lodash/forIn"
import usePageIdleDetection from "hooks/usePageIdleDetection"
import { useLazyGetBulkTasksQuery } from "queries/bulkUploadApi"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { fetchBulkTasks } from "utils/rtk_migration_helper"
import { enableBulkUploadProcessingBannerByUploadTypeValue } from "constants/menu"
import { useGetUploadType } from "../../bulkUploadDrawer/hooks/useGetUploadType"

enum BULK_TASK_FILTER_BY {
  STATUS = "STATUS",
}
const BULK_TASK_FILTER_MAPPING: Record<
  BULK_TASK_FILTER_BY,
  keyof BulkUploadTask
> = {
  [BULK_TASK_FILTER_BY.STATUS]: "status",
}

export const useBulkUploadTasks = () => {
  const dispatch = useDispatch()
  const {
    merchantID,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
  } = useSelector((state: AppState) => state.menu)
  const [displayedBulkUploadTasks, setDisplayedBulkUploadTasks] = useState<
    BulkUploadTask[]
  >([])
  const [filters, setFilters] = useState<Record<BULK_TASK_FILTER_BY, string>>({
    [BULK_TASK_FILTER_BY.STATUS]: "",
  })
  const { isFetching, data } = useSelector(
    (state: AppState) => state.bulkUpload?.bulkTasks,
  )

  const { ongoingBulkTasks } = useSelector(
    (state: AppState) => state.bulkUpload,
    shallowEqual,
  )

  const {
    flags: { fetchBulkTasks: isRtkFetchBulkTasksEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [fetchBulkTasksRtk] = useLazyGetBulkTasksQuery()

  const { tasks: allBulkUploadTasks } = data || {}

  const uploadType = useGetUploadType()

  // Check if the global banner is currently polling to avoid duplicate polling
  const enableBulkUploadProcessingBanner =
    enableBulkUploadProcessingBannerByUploadTypeValue[uploadType]
  const isProcessingBannerShown =
    enableBulkUploadProcessingBanner && ongoingBulkTasks.length > 0

  const isFetchingRef = useRef(isFetching)
  const isProcessingBannerShownRef = useRef(isProcessingBannerShown)

  const startFetchingBulkTasks = useCallback(async () => {
    const requestParams = {
      menuGroupID,
      uploadType: uploadType as UploadType,
      limit: BULK_UPLOAD_TABLE_RECORD_LIMIT,
      startCursor: "",
    }

    await fetchBulkTasks({
      dispatch,
      merchantID,
      merchantGroupID,
      requestParams,
      isRtkFlagLoading,
      isRtkFetchBulkTasksEnabled,
      fetchBulkTasksRtk,
      dispatchLegacyFetchBulkTasks: () =>
        dispatch(
          actions.fetchBulkTasks(requestParams, merchantID, merchantGroupID),
        ),
    })
  }, [
    dispatch,
    uploadType,
    merchantID,
    merchantGroupID,
    menuGroupID,
    isRtkFlagLoading,
    isRtkFetchBulkTasksEnabled,
    fetchBulkTasksRtk,
  ])

  const stopFetchingBulkTasks = useCallback(() => {
    dispatch({
      type: actionTypes.CANCEL_POLLING_BULK_TASKS,
    })
  }, [dispatch])

  usePageIdleDetection({
    startIdlingExecution: stopFetchingBulkTasks,
    backToActiveExecution: startFetchingBulkTasks,
    waitTime: BULK_TASK_STOP_POLLING_IDLE_WAIT_TIME,
    isDisabled: isProcessingBannerShown,
  })

  useEffect(() => {
    isFetchingRef.current = isFetching
  }, [isFetching])

  useEffect(() => {
    isProcessingBannerShownRef.current = isProcessingBannerShown
  }, [isProcessingBannerShown])

  useEffect(() => {
    setTimeout(() => {
      // wait until fetch store list finished  and assign the value to uploadType first
      if (
        uploadType &&
        !isFetchingRef.current &&
        !isProcessingBannerShownRef.current
      ) {
        // start fetching for history table, also trigger polling if any ongoing task exists
        startFetchingBulkTasks()
      }
    }, 0)
  }, [startFetchingBulkTasks, uploadType])

  useEffect(() => {
    return () => {
      if (!enableBulkUploadProcessingBanner) {
        stopFetchingBulkTasks()
      }
    }
  }, [enableBulkUploadProcessingBanner, stopFetchingBulkTasks])

  useEffect(() => {
    let filteredBulkTasks = [...allBulkUploadTasks] // avoid changing reference of allBulkUploadTasks
    _forIn(filters, (filterByValue: string, filterBy: BULK_TASK_FILTER_BY) => {
      if (!filterByValue) return

      filteredBulkTasks = filteredBulkTasks.filter(
        (task: BulkUploadTask) =>
          task[BULK_TASK_FILTER_MAPPING[filterBy]] === filterByValue,
      )
    })

    setDisplayedBulkUploadTasks(filteredBulkTasks)
  }, [allBulkUploadTasks, filters])

  const onFilterByTaskStatus = (newStatus: BulkUploadTaskStatus) => {
    setFilters({
      ...filters,
      [BULK_TASK_FILTER_BY.STATUS]: newStatus,
    })
  }

  return {
    isFetching,
    stopFetchingBulkTasks,
    bulkUploadTasks: displayedBulkUploadTasks,
    onFilterByTaskStatus,
  }
}
